import * as React from 'react';
import {
  autoFormatDateInput,
  getMaxDigits,
  parseDateInputFormat,
} from '../../date-range-picker/utils/inputFormat';

type UseSingleDateTextParams = {
  value: Date | undefined;
  format: (date: Date) => string;
  parse: (text: string) => Date | undefined;
  displayFormat: string;
  onCommit: (next: Date | undefined) => void;
  onBlur?: (value: Date | undefined) => void;
  onComplete?: () => void;
};

const countDigits = (text: string) => text.replace(/\D/g, '').length;

export function useSingleDateText({
  value,
  format,
  parse,
  displayFormat,
  onCommit,
  onBlur,
  onComplete,
}: UseSingleDateTextParams) {
  const tokens = React.useMemo(
    () => parseDateInputFormat(displayFormat),
    [displayFormat],
  );
  const maxDigits = React.useMemo(() => getMaxDigits(tokens), [tokens]);

  const [text, setText] = React.useState(value ? format(value) : '');

  React.useEffect(() => {
    setText(value ? format(value) : '');
  }, [format, value]);

  const handleChange = React.useCallback(
    (raw: string) => {
      const formatted = autoFormatDateInput(raw, tokens);
      const wasComplete = countDigits(text) === maxDigits;
      const isComplete = countDigits(formatted) === maxDigits;
      setText(formatted);
      if (!wasComplete && isComplete) {
        const parsed = parse(formatted);
        if (parsed) {
          onCommit(parsed);
          // Defer so the state update is committed before focus shifts —
          // otherwise the blur handler reads the previous (stale) text.
          setTimeout(() => onComplete?.(), 0);
        }
      }
    },
    [maxDigits, onCommit, onComplete, parse, text, tokens],
  );

  const handleBlur = React.useCallback(() => {
    if (!text) {
      onCommit(undefined);
      onBlur?.(undefined);
      return;
    }
    const parsed = parse(text);
    if (parsed) {
      onCommit(parsed);
      onBlur?.(parsed);
      return;
    }
    // Revert to the last valid value when the typed text can't be parsed.
    setText(value ? format(value) : '');
  }, [format, onBlur, onCommit, parse, text, value]);

  return {text, setText, handleChange, handleBlur};
}
